/* eslint-disable eqeqeq */
// @ts-nocheck
import { CForm, CFormInput, CFormSelect, CSpinner } from "@coreui/react";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useToast } from "../../../../hooks/useToast";
import { useCookie } from "../../../../hooks/useCookie";
import apiService from "../../../../service/apiService";
import { general } from "../../../../locales/general";
import BoxWrapper from "../../../../components/ui/BoxWrapper";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormMode } from "../../../../helpers/enums";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { isNotEmpty, verifyFileList } from "../../../../helpers/general";

import {
  createZoneAwareDate,
  toDateInputValue,
} from "../../../../helpers/general";
import { AxiosError } from "axios";
import { useAuth } from "../../../../hooks/useAuth";
import { useSelectedPlace } from "../../../../hooks/place/useSelectedPlace";
import TimeSelectInput from "../../../../components/inputs/TimeSelectInput";

interface TicketDateSlot {
  date: string;
  startTime?: string | null;
}

interface ServiceType {
  available: number;
  label: string;
  picture: File;
  startingPrice: number;
  otherInfo: string | null;
  ticketsDates?: TicketDateSlot[];
}

const schema = yup.object({
  label: yup.string().required("Ce champ est requis"),
  startingPrice: yup
    .number()
    .nullable()
    .typeError("")
    .transform((_, val) => {
      const result = val == Number(val) ? Number(val) : null;
      return result;
    }),
  startingPriceEuro: yup
    .number()
    .nullable()
    .typeError("")
    .transform((_, val) => {
      const result = val == Number(val) ? Number(val) : null;
      return result;
    }),
  available: yup
    .number()
    .typeError("")
    .default(0)
    .required("Ce champ est requis"),
  isVisible: yup.string().required("Ce champ est requis"),
  otherInfo: yup.string().nullable(),
  picture: yup.mixed().nullable(),
  disabled: yup.string().required("Ce champ est obligatoire"),
  order: yup
    .number()
    // @ts-ignore
    .nullable(true)
    .typeError("")
    .transform((_, val) => {
      if (val == "") return null;
      const result = val == Number(val) ? Number(val) : null;
      return result;
    }),
  ticketsDates: yup.array().of(
    yup.object({
      date: yup.string().required("La date est obligatoire"),
      startTime: yup.string().nullable().optional(),
    }),
  ),
});

export default function EventNewService({ mode = FormMode.CREATE }) {
  const { toastSuccess, toastError } = useToast();
  const [formLoading, setFormLoading] = useState(false);
  const { token } = useCookie("vToken");
  const navigate = useNavigate();
  const params = useParams();
  const { isAdmin, user } = useAuth();
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  const {
    register,
    handleSubmit,
    control: formControl,
    formState: { errors },
    setValue: setFormValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const {
    fields: eventTicketsFields,
    append: appendEventTicketsField,
    remove: removeEventTicketsField,
  } = useFieldArray({
    control: formControl,
    name: "ticketsDates",
  });

  const createNewServiceMutation = useMutation(async (data: ServiceType) => {
    return await apiService.MakePostRequest(
      `events/service?placeId=${selectedPlaceId}`,
      data,
      token,
      true,
    );
  });

  const updateServiceMutation = useMutation((data: Partial<ServiceType>) => {
    return apiService.MakePutRequest(
      `services/${params.serviceId}?placeId=${selectedPlaceId}`,
      data,
      token,
      true,
    );
  });

  const formatTicketDates = (ticketsDates: any[]) => {
    if (!Array.isArray(ticketsDates)) return null;
    return ticketsDates
      .filter((slot) => Boolean(slot?.date))
      .map((slot) => ({
        date:
          slot.date instanceof Date
            ? slot.date.toISOString().split("T")[0]
            : slot.date,
        startTime: slot.startTime ? slot.startTime : null,
      }));
  };

  const createService = async (data: any) => {
    try {
      data.eventId = params.eventId;
      data.ticketsDates = formatTicketDates(data.ticketsDates);
      setFormLoading(true);
      await createNewServiceMutation.mutateAsync(data);
      toastSuccess(general.fr.message.serviceAdded);
      navigate(`/events/${params.eventId}/services`);
    } catch (error) {
      let errorMessage = general.fr.message.operationFailed;
      if (error instanceof AxiosError) {
        const errorData = error?.response?.data as VenezApiError;
        if (isNotEmpty(errorData?.message)) {
          errorMessage = errorData?.message;
        }
      }
      setFormLoading(false);
      toastError(errorMessage);
    }
  };

  const updateService = async (data: any) => {
    try {
      setFormLoading(true);
      data.ticketsDates = formatTicketDates(data.ticketsDates);
      await updateServiceMutation.mutateAsync(data);
      toastSuccess(general.fr.message.serviceUpdated);
      navigate(`/events/${params.eventId}/services`);
    } catch (error) {
      setFormLoading(false);
      toastError(general.fr.message.operationFailed);
    }
  };

  const onSubmit = async (dataArg: any) => {
    const data = { ...dataArg };
    try {
      data.placeId = params.placeId;
      data.subCategoryId = data.subCategoryId?.id;
      const imageFile = verifyFileList(data.picture);
      data.picture = imageFile;

      if (mode === FormMode.UPDATE) {
        updateService(data);
      } else {
        createService(data);
      }
    } catch (error) {
      setFormLoading(false);
      toastError(general.fr.message.operationFailed);
    }
  };

  useEffect(() => {
    if (mode === FormMode.UPDATE && params?.serviceId) {
      const formFields = Object.keys(schema.fields);
      apiService
        .MakeGetRequest(`services/${params.serviceId}`, token)
        .then((response) => {
          setFormValue("picture", response.image);
          Object.entries(response).forEach((entry) => {
            if (formFields.indexOf(entry[0]) !== -1) {
              if (entry[0] === "ticketsDates") {
                const ticketsDates = entry[1]?.map((ticketSlot: any) => {
                  // Support both legacy string dates and new object structure { date, startTime }
                  if (typeof ticketSlot === "object" && ticketSlot !== null) {
                    return {
                      date: ticketSlot.date
                        ? toDateInputValue(
                            createZoneAwareDate(new Date(ticketSlot.date)),
                          )
                        : "",
                      startTime: ticketSlot.startTime || "",
                    };
                  }
                  return {
                    date: toDateInputValue(
                      createZoneAwareDate(new Date(ticketSlot)),
                    ),
                    startTime: "",
                  };
                });
                setFormValue("ticketsDates", ticketsDates);
              } else if (entry[0] === "date") {
                setFormValue(
                  entry[0],
                  toDateInputValue(createZoneAwareDate(new Date(entry[1]))),
                );
              } else {
                setFormValue(entry[0], entry[1]);
              }
            }
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className=" mt-4 ">
        <BoxWrapper>
          <section className="p-4">
            <div className="border-bottom border-success">
              <h4>
                {mode === FormMode.UPDATE
                  ? "Mise à jour le service"
                  : "Ajouter un nouveau service"}
              </h4>
            </div>
            <CForm
              validated={true}
              className="row form mt-4"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="col-md-6 mt-4">
                <label htmlFor="label" className="d-block">
                  Nom du service <span className="text-sm text-red">*</span>
                </label>
                <input
                  type="text"
                  className="custom-input"
                  placeholder="Nom du service"
                  id="label"
                  required
                  {...register("label")}
                />
                {errors.label?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.label.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 mt-4">
                <label htmlFor="otherInfo" className="d-block">
                  Autres informations
                </label>
                <input
                  type="text"
                  className="custom-input"
                  placeholder="Service autres informations"
                  id="otherInfo"
                  {...register("otherInfo")}
                />
                {errors.otherInfo?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.otherInfo.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 mt-4">
                <label htmlFor="startingPrice" className="d-block">
                  Prix de base <span className="text-sm text-red">*</span>
                </label>
                <input
                  type="number"
                  className="custom-input"
                  placeholder="Prix de base"
                  id="startingPrice"
                  required
                  {...register("startingPrice")}
                  step="0.01"
                />
                {errors.startingPrice?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.startingPrice.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 mt-4">
                <label htmlFor="startingPriceEuro" className="d-block">
                  Prix de base (Euro)
                </label>
                <input
                  type="number"
                  className="custom-input"
                  placeholder="Prix de base"
                  id="startingPriceEuro"
                  {...register("startingPriceEuro")}
                  step="0.01"
                />
                {errors.startingPriceEuro?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.startingPriceEuro.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 mt-4">
                <label htmlFor="available" className="d-block">
                  Espace disponible <span className="text-sm text-red">*</span>
                </label>
                <input
                  type="number"
                  className="custom-input"
                  placeholder="Espace disponible"
                  id="available"
                  required
                  {...register("available")}
                />
                {errors.available?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.available.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 mt-4">
                <label htmlFor="picture" className="d-block">
                  Image
                </label>
                <CFormInput
                  type="file"
                  id="picture"
                  className="my-3"
                  {...register("picture")}
                />
                {errors.picture?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.picture.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 mt-4">
                <label htmlFor="disabled" className="d-block">
                  Désactivé <span className="text-sm text-red">*</span>
                </label>
                <CFormSelect
                  className="my-3"
                  id="disabled"
                  {...register("disabled")}
                >
                  <option selected value="">
                    Choisissez une option
                  </option>
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
                </CFormSelect>
                {errors.disabled?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.disabled.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 mt-4">
                <label htmlFor="isVisible" className="d-block">
                  Visible <span className="text-sm text-red">*</span>
                </label>
                <CFormSelect
                  className="my-3"
                  id="isVisible"
                  {...register("isVisible")}
                >
                  <option selected value="">
                    Choisissez une option
                  </option>
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
                </CFormSelect>
                {errors.isVisible?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.isVisible.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 mt-4">
                <label htmlFor="order" className="d-block">
                  Ordre
                </label>
                <input
                  type="number"
                  className="custom-input"
                  id="order"
                  {...register("order")}
                />
                {errors.order?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.order.message}
                  </div>
                )}
              </div>

              {}
              <section className="mt-5">
                <h5>Billets d'événement</h5>
              </section>
              {eventTicketsFields.map((eventTicket, index) => {
                return (
                  <div className="col-md-12 mt-3" key={eventTicket.id}>
                    <div className="row align-items-end">
                      <div className="col-md-5">
                        <label className="d-block">
                          Date du billet <span className="text-red">*</span>
                        </label>
                        <CFormInput
                          type="date"
                          className="custom-input"
                          placeholder="Date"
                          {...register(`ticketsDates.${index}.date`)}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="d-block">
                          Heure de début (Optionnel)
                        </label>
                        <Controller
                          name={`ticketsDates.${index}.startTime`}
                          control={formControl}
                          render={({ field }) => {
                            return (
                              <TimeSelectInput
                                label=""
                                selectedTime={field.value!}
                                onChange={field.onChange}
                                numOfHours={24}
                              />
                            );
                          }}
                        />
                      </div>
                      <div className="col-md-3">
                        <button
                          type="button"
                          className="btn btn-danger w-100 text-white"
                          onClick={() => removeEventTicketsField(index)}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                    {errors.ticketsDates?.[index]?.date?.message && (
                      <div
                        className="text-red-500 text-opacity-50 mt-1"
                        style={{ color: "red" }}
                      >
                        {errors.ticketsDates[index].date.message}
                      </div>
                    )}
                  </div>
                );
              })}

              <section className="mt-5">
                <button
                  type="button"
                  className="btn btn-info green-shadow w-20 text-white"
                  onClick={() => {
                    appendEventTicketsField({ date: "", startTime: "" });
                  }}
                >
                  Ajouter un billet
                </button>
              </section>

              <section className="d-flex justify-content-center buttons gap-4 mt-5">
                <Link
                  to={`/events/${params?.eventId}/services`}
                  className="btn btn-danger red-shadow w-20 px-4 py-2 text-white"
                >
                  Annuler
                </Link>
                <button
                  className="btn btn-success green-shadow w-20 text-white"
                  type="submit"
                  disabled={formLoading}
                >
                  {formLoading ? (
                    <div className="text-center">
                      <CSpinner size="sm" />
                    </div>
                  ) : (
                    "Enregistrer"
                  )}
                </button>
              </section>
            </CForm>
          </section>
        </BoxWrapper>
      </div>
    </>
  );
}
